import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { ArrowLeft, Calendar as CalendarIcon, Building2, Clock, X } from 'lucide-react';
import { format, parseISO, startOfDay, endOfDay, differenceInMinutes, eachDayOfInterval, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_comunelaval-halls/artifacts/khvga6ci_Cuns%C3%ABi%20de%20Comun%2020251017_124442.jpg";

export default function PublicCalendar() {
  const { hallId } = useParams();
  const navigate = useNavigate();
  const [hall, setHall] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hallsRes, bookingsRes] = await Promise.all([
          axios.get(`${API_URL}/halls`),
          axios.get(`${API_URL}/halls/${hallId}/bookings`)
        ]);
        setHall(hallsRes.data.find(h => h.id === hallId));
        setBookings(bookingsRes.data);
      } catch (error) {
        console.error('Errore caricamento', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [hallId]);

  const { bookedLight, bookedMedium, bookedHeavy, bookedFull } = useMemo(() => {
    const light = [], medium = [], heavy = [], full = [];
    const dayMap = new Map();
    bookings.forEach(booking => {
      if (!booking.start_datetime || !booking.end_datetime) return;
      const bStart = parseISO(booking.start_datetime);
      const bEnd = parseISO(booking.end_datetime);
      if (bEnd <= bStart) return;
      const days = eachDayOfInterval({ start: startOfDay(bStart), end: startOfDay(bEnd) });
      days.forEach(day => {
        const dayS = startOfDay(day);
        const dayE = endOfDay(day);
        const overlapStart = bStart > dayS ? bStart : dayS;
        const overlapEnd = bEnd < dayE ? bEnd : dayE;
        if (overlapEnd <= overlapStart) return;
        const mins = differenceInMinutes(overlapEnd, overlapStart);
        const key = format(day, 'yyyy-MM-dd');
        dayMap.set(key, (dayMap.get(key) || 0) + mins);
      });
    });
    dayMap.forEach((totalMins, key) => {
      const fraction = Math.min(totalMins / (24 * 60), 1);
      const date = parseISO(key);
      if (fraction >= 0.95) full.push(date);
      else if (fraction >= 0.67) heavy.push(date);
      else if (fraction >= 0.34) medium.push(date);
      else if (fraction > 0) light.push(date);
    });
    return { bookedLight: light, bookedMedium: medium, bookedHeavy: heavy, bookedFull: full };
  }, [bookings]);

  const bookingsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    return bookings.filter(b => {
      if (!b.start_datetime || !b.end_datetime) return false;
      const bStart = parseISO(b.start_datetime);
      const bEnd = parseISO(b.end_datetime);
      if (bEnd <= bStart) return false;
      const days = eachDayOfInterval({ start: startOfDay(bStart), end: startOfDay(bEnd) });
      return days.some(d => isSameDay(d, selectedDate));
    });
  }, [selectedDate, bookings]);

  const handleDayClick = (day) => {
    if (!day) return;
    setSelectedDate(prev => prev && isSameDay(prev, day) ? null : day);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Caricamento...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/login')} data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
          </Button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Comune di La Val" className="w-8 h-8 rounded-full object-cover" />
            <span className="font-heading font-bold text-lg">Salf manifestaziuns</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-2">
            <h1 className="font-heading font-extrabold text-3xl sm:text-5xl tracking-tight">{hall?.name}</h1>
            <p className="text-lg text-muted-foreground">{hall?.description}</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-2xl flex items-center gap-2">
                <CalendarIcon className="w-6 h-6" /> Calendario Disponibilità
              </CardTitle>
              <CardDescription>Il colore rosso indica le ore prenotate della giornata</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-6">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDayClick}
                locale={it}
                className="rounded-lg border p-4 w-full max-w-md"
                modifiers={{ bookedLight, bookedMedium, bookedHeavy, bookedFull }}
                modifiersStyles={{
                  bookedLight: { background: 'linear-gradient(to top, hsl(0 70% 55% / 0.35) 25%, transparent 25%)', fontWeight: 600 },
                  bookedMedium: { background: 'linear-gradient(to top, hsl(0 70% 55% / 0.40) 50%, transparent 50%)', fontWeight: 700 },
                  bookedHeavy: { background: 'linear-gradient(to top, hsl(0 70% 55% / 0.45) 75%, transparent 75%)', fontWeight: 700, color: 'hsl(0 60% 35%)' },
                  bookedFull: { background: 'hsl(0 70% 55% / 0.45)', fontWeight: 700, color: 'hsl(0 60% 25%)', opacity: 0.9 },
                }}
                data-testid="public-calendar"
              />

              {/* Selected day bookings */}
              {selectedDate && (
                <div className="w-full max-w-md space-y-3" data-testid="selected-day-bookings">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm sm:text-base">
                      Prenotazioni del {format(selectedDate, 'dd MMMM yyyy', { locale: it })}
                    </h3>
                    <button onClick={() => setSelectedDate(null)} className="p-1 rounded-full hover:bg-muted" data-testid="close-day-bookings">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {bookingsForSelectedDate.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted text-center">Nessuna prenotazione per questa giornata</p>
                  ) : (
                    bookingsForSelectedDate.map(b => (
                      <div key={b.id} className="p-3 rounded-lg border bg-card space-y-1.5" data-testid={`day-booking-${b.id}`}>
                        <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                          <Clock className="w-4 h-4 shrink-0" />
                          <span>{format(parseISO(b.start_datetime), 'HH:mm')} - {format(parseISO(b.end_datetime), 'HH:mm')}</span>
                          {!isSameDay(parseISO(b.start_datetime), parseISO(b.end_datetime)) && (
                            <span className="text-xs text-muted-foreground">({format(parseISO(b.start_datetime), 'dd/MM')} - {format(parseISO(b.end_datetime), 'dd/MM')})</span>
                          )}
                        </div>
                        {b.user_info?.societa_associazione && (
                          <div className="flex items-center gap-2 text-sm">
                            <Building2 className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                            <span className="font-medium">{b.user_info.societa_associazione}</span>
                          </div>
                        )}
                        {b.descrizione_manifestazione && (
                          <p className="text-xs text-muted-foreground pl-6">{b.descrizione_manifestazione}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm">
                <div className="flex items-center gap-2"><div className="w-6 h-6 rounded border" /><span>Libero</span></div>
                <div className="flex items-center gap-2"><div className="w-6 h-6 rounded border" style={{ background: 'linear-gradient(to top, hsl(0 70% 55% / 0.35) 25%, transparent 25%)' }} /><span>Poco</span></div>
                <div className="flex items-center gap-2"><div className="w-6 h-6 rounded border" style={{ background: 'linear-gradient(to top, hsl(0 70% 55% / 0.40) 50%, transparent 50%)' }} /><span>Metà</span></div>
                <div className="flex items-center gap-2"><div className="w-6 h-6 rounded border" style={{ background: 'linear-gradient(to top, hsl(0 70% 55% / 0.45) 75%, transparent 75%)' }} /><span>Molto</span></div>
                <div className="flex items-center gap-2"><div className="w-6 h-6 rounded border" style={{ background: 'hsl(0 70% 55% / 0.45)' }} /><span>Pieno</span></div>
              </div>
            </CardContent>
          </Card>

          {/* Booking List */}
          {bookings.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="font-heading text-xl">Prenotazioni Esistenti</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="p-3 sm:p-4 rounded-lg bg-muted" data-testid={`booking-item-${booking.id}`}>
                      {booking.start_datetime && booking.end_datetime ? (
                        <div className="space-y-1">
                          <p className="font-medium text-sm sm:text-base">
                            {format(parseISO(booking.start_datetime), 'dd/MM/yyyy HH:mm', { locale: it })} - {format(parseISO(booking.end_datetime), 'dd/MM/yyyy HH:mm', { locale: it })}
                          </p>
                          {booking.user_info?.societa_associazione && (
                            <div className="flex items-center gap-2 text-primary">
                              <Building2 className="w-4 h-4" />
                              <span className="font-semibold text-sm">{booking.user_info.societa_associazione}</span>
                            </div>
                          )}
                          <p className="text-sm text-muted-foreground">{booking.descrizione_manifestazione}</p>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="text-center">
            <Button onClick={() => navigate('/login')} size="lg" className="btn-scale">
              Accedi per prenotare
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
